'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  Check,
  Search,
  Crown,
  Star,
  UserCheck,
  Users,
  UserPlus,
  Loader2,
} from 'lucide-react';
import { UserDomain } from '@/lib/services/domain-switching-service';

interface SidebarDomainDropdownProps {
  activeDomain: UserDomain | null;
  domains: UserDomain[];
  domainsInfo: Record<string, { plan_type?: string; credits?: number }>;
  onSwitch: (domainId: string) => void;
  isSwitching: boolean;
  isCollapsed: boolean;
  onExpand?: () => void;
}

export function SidebarDomainDropdown({
  activeDomain,
  domains,
  domainsInfo,
  onSwitch,
  isSwitching,
  isCollapsed,
  onExpand,
}: SidebarDomainDropdownProps) {
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const getDomainInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-3 w-3" />;
      case 'admin':
        return <UserCheck className="h-3 w-3" />;
      case 'contributor':
        return <Users className="h-3 w-3" />;
      default:
        return <UserPlus className="h-3 w-3" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const activePlan = activeDomain
    ? domainsInfo[activeDomain.domain_id]?.plan_type || 'free'
    : 'free';

  const personalDomain = domains.find((d) => d.role === 'owner');
  const memberDomains = domains.filter((d) => d.role !== 'owner');

  const handleSwitch = (domainId: string) => {
    onSwitch(domainId);
    setCommandOpen(false);
  };

  if (!activeDomain) {
    return (
      <div className="flex items-center justify-center p-3">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const handleCollapsedClick = () => {
    if (isCollapsed && onExpand) {
      onExpand();
    }
  };

  const dropdownTrigger = (
    <Button
      variant="ghost"
      className={cn(
        'w-full justify-start gap-3 h-auto py-3',
        isCollapsed && 'justify-center px-0'
      )}
      disabled={isSwitching}
      onClick={isCollapsed ? handleCollapsedClick : undefined}
    >
      <div className="relative">
        <Avatar className={cn('transition-all', isCollapsed ? 'h-10 w-10' : 'h-12 w-12')}>
          <AvatarImage src={activeDomain.domain_logo_url} />
          <AvatarFallback
            style={{ backgroundColor: activeDomain.domain_theme_color }}
            className={cn('font-medium', isCollapsed ? 'text-sm' : 'text-base')}
          >
            {getDomainInitials(activeDomain.domain_name)}
          </AvatarFallback>
        </Avatar>
        {activePlan !== 'free' && (
          <div
            className={cn(
              'absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center',
              activePlan === 'pro' &&
                'bg-gradient-to-br from-apprendo-500 to-apprendo-600 shadow-lg shadow-apprendo-500/30',
              activePlan === 'plus' &&
                'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30'
            )}
          >
            {activePlan === 'pro' ? (
              <Crown className="h-3 w-3 text-white" />
            ) : (
              <Star className="h-3 w-3 text-white" />
            )}
          </div>
        )}
      </div>
      {!isCollapsed && (
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium truncate">{activeDomain.domain_name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            {activePlan !== 'free' && (
              <span className="uppercase font-semibold">{activePlan}</span>
            )}
            {activeDomain.domain_handle && (
              <>
                {activePlan !== 'free' && <span>·</span>}
                <span className="truncate">@{activeDomain.domain_handle}</span>
              </>
            )}
          </div>
        </div>
      )}
      {!isCollapsed && (
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      )}
    </Button>
  );

  if (isCollapsed) {
    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-center px-0 h-auto py-3"
              disabled={isSwitching}
              onClick={() => onExpand?.()}
            >
              <div className="relative">
                <Avatar className="h-10 w-10 transition-all">
                  <AvatarImage src={activeDomain.domain_logo_url} />
                  <AvatarFallback
                    style={{ backgroundColor: activeDomain.domain_theme_color }}
                    className="text-sm font-medium"
                  >
                    {getDomainInitials(activeDomain.domain_name)}
                  </AvatarFallback>
                </Avatar>
                {activePlan !== 'free' && (
                  <div
                    className={cn(
                      'absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center',
                      activePlan === 'pro' &&
                        'bg-gradient-to-br from-apprendo-500 to-apprendo-600 shadow-lg shadow-apprendo-500/30',
                      activePlan === 'plus' &&
                        'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30'
                    )}
                  >
                    {activePlan === 'pro' ? (
                      <Crown className="h-3 w-3 text-white" />
                    ) : (
                      <Star className="h-3 w-3 text-white" />
                    )}
                  </div>
                )}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">{activeDomain.domain_name}</p>
            <p className="text-xs text-muted-foreground">Click to expand & switch domain</p>
          </TooltipContent>
        </Tooltip>

        <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
          <CommandInput placeholder="Search domains..." />
          <CommandList>
            <CommandEmpty>No domains found.</CommandEmpty>
            {personalDomain && (
              <CommandGroup heading="Personal">
                <CommandItem
                  onSelect={() => handleSwitch(personalDomain.domain_id)}
                  disabled={isSwitching}
                >
                  <Avatar className="h-6 w-6 mr-2">
                    <AvatarImage src={personalDomain.domain_logo_url} />
                    <AvatarFallback
                      style={{ backgroundColor: personalDomain.domain_theme_color }}
                      className="text-xs"
                    >
                      {getDomainInitials(personalDomain.domain_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span>{personalDomain.domain_name}</span>
                  {activeDomain?.domain_id === personalDomain.domain_id && (
                    <Check className="h-4 w-4 ml-auto" />
                  )}
                </CommandItem>
              </CommandGroup>
            )}
            {memberDomains.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Teams">
                  {memberDomains.map((domain) => (
                    <CommandItem
                      key={domain.domain_id}
                      onSelect={() => handleSwitch(domain.domain_id)}
                      disabled={isSwitching}
                    >
                      <Avatar className="h-6 w-6 mr-2">
                        <AvatarImage src={domain.domain_logo_url} />
                        <AvatarFallback
                          style={{ backgroundColor: domain.domain_theme_color }}
                          className="text-xs"
                        >
                          {getDomainInitials(domain.domain_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 flex items-center gap-2">
                        <span>{domain.domain_name}</span>
                        <Badge
                          variant={getRoleBadgeVariant(domain.role)}
                          className="h-5 text-xs"
                        >
                          {domain.role}
                        </Badge>
                      </div>
                      {activeDomain?.domain_id === domain.domain_id && (
                        <Check className="h-4 w-4 ml-auto" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </CommandDialog>
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {dropdownTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[280px]">
          <DropdownMenuLabel>Switch Domain</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {personalDomain && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Personal
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => handleSwitch(personalDomain.domain_id)}
                disabled={isSwitching}
                className="cursor-pointer"
              >
                <Avatar className="h-8 w-8 mr-3">
                  <AvatarImage src={personalDomain.domain_logo_url} />
                  <AvatarFallback
                    style={{ backgroundColor: personalDomain.domain_theme_color }}
                    className="text-xs"
                  >
                    {getDomainInitials(personalDomain.domain_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">
                      {personalDomain.domain_name}
                    </span>
                    {domainsInfo[personalDomain.domain_id]?.plan_type &&
                      domainsInfo[personalDomain.domain_id]?.plan_type !== 'free' && (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'h-5 text-[10px] px-1.5',
                            domainsInfo[personalDomain.domain_id]?.plan_type === 'pro' &&
                              'bg-apprendo-100 text-apprendo-700 dark:bg-apprendo-900/30 dark:text-apprendo-300',
                            domainsInfo[personalDomain.domain_id]?.plan_type === 'plus' &&
                              'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          )}
                        >
                          {domainsInfo[personalDomain.domain_id]?.plan_type?.toUpperCase()}
                        </Badge>
                      )}
                  </div>
                </div>
                {activeDomain?.domain_id === personalDomain.domain_id && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            </>
          )}

          {memberDomains.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Teams
              </DropdownMenuLabel>
              {memberDomains.map((domain) => (
                <DropdownMenuItem
                  key={domain.domain_id}
                  onClick={() => handleSwitch(domain.domain_id)}
                  disabled={isSwitching}
                  className="cursor-pointer"
                >
                  <Avatar className="h-8 w-8 mr-3">
                    <AvatarImage src={domain.domain_logo_url} />
                    <AvatarFallback
                      style={{ backgroundColor: domain.domain_theme_color }}
                      className="text-xs"
                    >
                      {getDomainInitials(domain.domain_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{domain.domain_name}</span>
                      {domainsInfo[domain.domain_id]?.plan_type &&
                        domainsInfo[domain.domain_id]?.plan_type !== 'free' && (
                          <Badge
                            variant="secondary"
                            className={cn(
                              'h-5 text-[10px] px-1.5',
                              domainsInfo[domain.domain_id]?.plan_type === 'pro' &&
                                'bg-apprendo-100 text-apprendo-700 dark:bg-apprendo-900/30 dark:text-apprendo-300',
                              domainsInfo[domain.domain_id]?.plan_type === 'plus' &&
                                'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            )}
                          >
                            {domainsInfo[domain.domain_id]?.plan_type?.toUpperCase()}
                          </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      {getRoleIcon(domain.role)}
                      <span className="capitalize">{domain.role}</span>
                    </div>
                  </div>
                  {activeDomain?.domain_id === domain.domain_id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setCommandOpen(true)}
            className="text-muted-foreground cursor-pointer"
          >
            <Search className="h-4 w-4 mr-2" />
            Quick Switch
            <span className="ml-auto text-xs">⌘K</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <CommandInput placeholder="Search domains..." />
        <CommandList>
          <CommandEmpty>No domains found.</CommandEmpty>
          {personalDomain && (
            <CommandGroup heading="Personal">
              <CommandItem
                onSelect={() => handleSwitch(personalDomain.domain_id)}
                disabled={isSwitching}
              >
                <Avatar className="h-6 w-6 mr-2">
                  <AvatarImage src={personalDomain.domain_logo_url} />
                  <AvatarFallback
                    style={{ backgroundColor: personalDomain.domain_theme_color }}
                    className="text-xs"
                  >
                    {getDomainInitials(personalDomain.domain_name)}
                  </AvatarFallback>
                </Avatar>
                <span>{personalDomain.domain_name}</span>
                {activeDomain?.domain_id === personalDomain.domain_id && (
                  <Check className="h-4 w-4 ml-auto" />
                )}
              </CommandItem>
            </CommandGroup>
          )}
          {memberDomains.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Teams">
                {memberDomains.map((domain) => (
                  <CommandItem
                    key={domain.domain_id}
                    onSelect={() => handleSwitch(domain.domain_id)}
                    disabled={isSwitching}
                  >
                    <Avatar className="h-6 w-6 mr-2">
                      <AvatarImage src={domain.domain_logo_url} />
                      <AvatarFallback
                        style={{ backgroundColor: domain.domain_theme_color }}
                        className="text-xs"
                      >
                        {getDomainInitials(domain.domain_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 flex items-center gap-2">
                      <span>{domain.domain_name}</span>
                      <Badge
                        variant={getRoleBadgeVariant(domain.role)}
                        className="h-5 text-xs"
                      >
                        {domain.role}
                      </Badge>
                    </div>
                    {activeDomain?.domain_id === domain.domain_id && (
                      <Check className="h-4 w-4 ml-auto" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
